import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { ChevronRight, ChevronLeft, X, Calendar, Home, Users, Settings, BarChart3, BedDouble, Scan } from 'lucide-react';

const TOUR_STEPS = [
  {
    title: 'Welcome to Keyless PMS!',
    desc: "Let's take a quick tour of your property management system. This will only take a minute.",
    icon: Home,
    color: 'bg-cyan-600'
  },
  {
    title: 'Dashboard',
    desc: "Your home base. See today's occupancy, revenue, arrivals, and departures at a glance. Navigate here from the sidebar.",
    icon: Home,
    color: 'bg-blue-600'
  },
  {
    title: 'Calendar & Reservations',
    desc: 'Create and manage reservations. Click any empty cell to book a room. Use the tape chart or room view. Scan guest IDs with a barcode scanner or webcam.',
    icon: Calendar,
    color: 'bg-emerald-600'
  },
  {
    title: 'Front Desk',
    desc: "Quick check-ins and check-outs. See today's arrivals, in-house guests, and departures. A folio is generated automatically after check-in.",
    icon: BedDouble,
    color: 'bg-purple-600'
  },
  {
    title: 'ID Scanning',
    desc: 'Scan guest IDs using: Tera D5100 barcode scanner, mag stripe reader, webcam camera, or USB scanner. Data auto-fills guest details.',
    icon: Scan,
    color: 'bg-amber-600'
  },
  {
    title: 'Guests & Reports',
    desc: 'Guest database stores all guest records automatically. Reports show daily occupancy, arrivals, departures, and revenue.',
    icon: Users,
    color: 'bg-red-600'
  },
  {
    title: 'Settings',
    desc: 'Configure your property, rooms, tax rates, scanner IP, and daily report email. Create staff accounts in the Staff page.',
    icon: Settings,
    color: 'bg-slate-600'
  },
  {
    title: "You're all set!",
    desc: "Start by going to Settings to set up your property and rooms. Then head to Calendar to create your first reservation. You can revisit this tour from Settings > Account.",
    icon: BarChart3,
    color: 'bg-gradient-to-r from-cyan-600 to-blue-600'
  }
];

const ProductTour = () => {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const tourDone = localStorage.getItem('product_tour_done');
    if (!tourDone) {
      // Small delay so the app loads first
      const timer = setTimeout(() => setShow(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const complete = () => {
    localStorage.setItem('product_tour_done', 'true');
    setShow(false);
  };

  const skip = () => {
    localStorage.setItem('product_tour_done', 'true');
    setShow(false);
  };

  if (!show) return null;

  const current = TOUR_STEPS[step];
  const Icon = current.icon;
  const isLast = step === TOUR_STEPS.length - 1;

  return (
    <Dialog open={show} onOpenChange={(open) => { if (!open) skip(); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        {/* Header with icon */}
        <div className={`${current.color} p-8 text-center text-white`}>
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-4">
            <Icon size={32} />
          </div>
          <h2 className="text-xl font-bold">{current.title}</h2>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-slate-600 text-sm leading-relaxed">{current.desc}</p>

          {/* Progress dots */}
          <div className="flex justify-center gap-1.5 my-4">
            {TOUR_STEPS.map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-full transition-all ${i === step ? 'bg-cyan-600 w-6' : 'bg-slate-200'}`} />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <div>
              {step > 0 ? (
                <Button variant="ghost" size="sm" onClick={() => setStep(step - 1)}>
                  <ChevronLeft size={16} className="mr-1" /> Back
                </Button>
              ) : (
                <Button variant="ghost" size="sm" onClick={skip} className="text-slate-400">
                  Skip Tour
                </Button>
              )}
            </div>
            <span className="text-xs text-slate-400">{step + 1} / {TOUR_STEPS.length}</span>
            <div>
              {isLast ? (
                <Button onClick={complete} className="bg-cyan-600 hover:bg-cyan-700">
                  Get Started
                </Button>
              ) : (
                <Button onClick={() => setStep(step + 1)}>
                  Next <ChevronRight size={16} className="ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Export a function to restart tour
export const restartTour = () => {
  localStorage.removeItem('product_tour_done');
  window.dispatchEvent(new Event('restart-product-tour'));
};

export default ProductTour;
